import { ReactElement, useState } from 'react';

import { Combobox } from '@base-ui/react/combobox';

import { useAriaId } from '@components/component-helpers';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { Stack } from '@components/stack';
import { useEvent } from '@hooks/use-event';
import { useTrackMenuOpen } from '@stores/menu-open-store';

import { highlightBehaviorProps, isSameOptionValue, optionLabelToString } from './highlight';
import { useSelectTranslations } from './locale';
import { NoOptions, OptionContent } from './option-content';
import { ResultCount, usePickAnnouncement } from './result-count';
import type { SelectOptionLabel } from './select';
import { SingleValueControl } from './single-value-control';
import { Item, List, Popup, Portal, Positioner } from './styles';
import { useAsyncOptions } from './use-async-options';
import { useEchoedSelectionInput } from './use-echoed-selection-input';

export type SelectAsyncOption<TValue = unknown> = {
  value: TValue;
  helpText?: string;
} & SelectOptionLabel;

type LoadOptionsCallback<TValue> = (options: SelectAsyncOption<TValue>[]) => void;

export interface SelectAsyncProps<TValue = unknown> {
  defaultValue?: SelectAsyncOption<TValue> | null;
  onChange: (value: TValue) => void;
  loadOptions: (
    inputValue: string,
    callback: LoadOptionsCallback<TValue>,
  ) => void | Promise<SelectAsyncOption<TValue>[]>;
  defaultOptions?: boolean | SelectAsyncOption<TValue>[];
  placeholder?: string;
  noOptions?: (obj: { inputValue: string }) => string | null;
  loadingMessage?: (obj: { inputValue: string }) => string | null;
  label?: string;
  labelIcon?: ReactElement<IconProps>;
  isDisabled?: boolean;
  prefix?: string;
}

// Results arrive already filtered by the server.
const acceptAll = () => true;

export function SelectAsync<TValue = unknown>(props: SelectAsyncProps<TValue>) {
  const { t } = useSelectTranslations();
  const {
    defaultValue,
    onChange,
    loadOptions,
    defaultOptions,
    placeholder = t('placeholder'),
    noOptions = () => t('no_options'),
    loadingMessage = () => t('loading'),
    label,
    labelIcon,
    isDisabled = false,
    prefix,
  } = props;

  const selectId = useAriaId('select-async');
  const [selected, setSelected] = useState<SelectAsyncOption<TValue> | null>(defaultValue ?? null);

  const echo = useEchoedSelectionInput({
    hasSelection: Boolean(selected),
    isClearable: false,
    placeholder,
    selectedText: selected ? optionLabelToString(selected) : null,
    overlayContent: selected ? selected.label : null,
  });

  const { items, isLoading, emptyMessage, search } = useAsyncOptions({
    loadOptions,
    defaultOptions,
    isOpen: echo.isOpen,
    inputValue: echo.query,
    noOptions,
    loadingMessage,
  });

  const pick = usePickAnnouncement(echo.isOpen);

  const handleValueChange = useEvent(
    (next: SelectAsyncOption<TValue> | null, details: Combobox.Root.ChangeEventDetails) => {
      if (next === null && echo.keepsSelectionOnEmptiedInput(details.reason)) return;
      setSelected(next);
      echo.endEditing();
      if (next) onChange(next.value);
      if (next) pick.announcePick(optionLabelToString(next));
      if (next) echo.blurAfterPickOnTouchScreen(details.event);
    },
  );

  const handleInputValueChange = useEvent(
    (next: string, details: Combobox.Root.ChangeEventDetails) => {
      const isQueryEdit = echo.handleInputValueChange(next, details);
      if (isQueryEdit) {
        pick.clearPick();
        search(next);
      }
    },
  );

  useTrackMenuOpen(echo.isOpen);

  return (
    <Stack space={1}>
      <FormLabel text={label} htmlFor={selectId} icon={labelIcon} />
      <Combobox.Root<SelectAsyncOption<TValue>, false>
        items={items}
        value={selected}
        open={echo.isOpen}
        onValueChange={handleValueChange}
        onOpenChange={echo.handleOpenChange}
        onInputValueChange={handleInputValueChange}
        filter={acceptAll}
        isItemEqualToValue={isSameOptionValue}
        itemToStringLabel={optionLabelToString}
        disabled={isDisabled}
        {...highlightBehaviorProps}
      >
        <SingleValueControl
          inputId={selectId}
          placeholder={placeholder}
          prefix={prefix}
          echo={echo}
          valueText={selected ? optionLabelToString(selected) : undefined}
          hasSelection={Boolean(selected)}
          isClearable={false}
          isDisabled={isDisabled}
          hasError={false}
          // base-ui doesn't open the list on focus by itself.
          onFocus={() => echo.setIsOpen(true)}
        />
        <ResultCount isAnnouncing={echo.isOpen && !isLoading} pickedLabel={pick.pickedLabel} />

        <Portal>
          <Positioner align="start">
            <Popup $isHiddenWhenEmpty={emptyMessage === null}>
              {emptyMessage !== null && <NoOptions>{emptyMessage}</NoOptions>}
              {/* Item keys are indexes — values may be objects, which don't
                  stringify to unique keys. */}
              <List>
                {(item: SelectAsyncOption<TValue>, index: number) => (
                  <Item key={index} value={item}>
                    <OptionContent label={item.label} helpText={item.helpText} hasReturnHint />
                  </Item>
                )}
              </List>
            </Popup>
          </Positioner>
        </Portal>
      </Combobox.Root>
    </Stack>
  );
}
